import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
} from "@mui/material";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventNoteIcon from "@mui/icons-material/EventNote";
import GroupsIcon from "@mui/icons-material/Groups";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  designColors,
  designRadii,
  designShell,
  designType,
  designWeights,
} from "services/constants/designTokens";
import { StatCard } from "pages/TeamZone/Home/homeUi";
import { statusMeta } from "../Leave/leaveHelpers";
import { useMyPendingRequests } from "pages/MyZone/Home/useMyPendingRequests";
import { LeaveDetailsDrawer } from "pages/MyZone/MyLeaves/LeaveDetailsDrawer";
import { TableEmptyState } from "components/CustomComponents/EmptyState";

// My Requests landing (image 2). The apply cards switch the inner view via the
// same keys MyZoneApply/index.js registers (onNavigate), so this page is the
// hub the rest of the module hangs off. Summary + recent list read the shared
// useMyPendingRequests source so the numbers here match the Home dashboard's.

const NAV = {
  leave: "Leaves",
  overtime: "Myzone Overtime",
  offday: "Myzone Off Day Duty",
  attendance: "Myzone WorkAdjustment",
  finance: "Advance Salary",
};

const requestCardSx = {
  ...designShell.sectionCard,
  p: 1.75,
  height: "100%",
};

const requestColors = {
  leave: "#0F766E",
  overtime: "#D97706",
  offday: "#7C3AED",
  attendance: "#2563EB",
  finance: "#0F766E",
  red: "#EF4444",
  gray: designColors.slate600,
};

// The three primary "apply for" cards.
const applyCards = [
  {
    key: NAV.leave,
    title: "Leave",
    desc: "Apply for leave and manage your leave requests",
    cta: "+ New Request",
    icon: BeachAccessIcon,
    color: requestColors.leave,
    tint: designColors.tealTint,
  },
  {
    key: NAV.overtime,
    title: "Overtime",
    desc: "Apply for overtime (pre-approved) or log completed overtime",
    cta: "+ New Request",
    icon: AccessTimeIcon,
    color: requestColors.overtime,
    tint: "#FFFAEB",
  },
  {
    key: NAV.offday,
    title: "Off-Day Duty",
    desc: "Apply for week off / holiday work or on-duty requests",
    cta: "+ New Request",
    icon: CalendarMonthIcon,
    color: requestColors.offday,
    tint: "#F4F3FF",
  },
];

const attendanceTiles = [
  { label: "Permission", icon: PersonPinCircleIcon },
  { label: "On Duty", icon: WorkOutlineIcon },
  { label: "Missing Punch", icon: FingerprintIcon },
  { label: "Schedule Change", icon: SwapHorizIcon },
  { label: "Comp Off", icon: EventAvailableIcon },
];

const financeTiles = [
  { label: "Salary Advance", icon: PaymentsIcon },
  { label: "Reimbursement", icon: ReceiptLongIcon },
];

const summaryTiles = [
  {
    key: "Pending",
    icon: HourglassEmptyIcon,
    accent: requestColors.overtime,
    tint: "#FFF7ED",
  },
  {
    key: "Approved",
    icon: CheckCircleOutlineIcon,
    accent: requestColors.leave,
    tint: designColors.tealTint,
  },
  {
    key: "Rejected",
    icon: CancelOutlinedIcon,
    accent: requestColors.red,
    tint: "#FEF2F2",
  },
  {
    key: "Cancelled",
    icon: RemoveCircleOutlineIcon,
    accent: requestColors.gray,
    tint: designColors.slate100,
  },
];

// "Leave Balance" opens the slide-in Leave Details drawer (action) instead of
// navigating to a page; the others navigate (Request Policy has no route).
const quickLinks = [
  { label: "Leave Balance", icon: EventNoteIcon, action: "leaveDetails" },
  { label: "Team Calendar", icon: GroupsIcon, path: "/admin/myzone/calendar" },
  { label: "Company Holidays", icon: EventAvailableIcon, path: "/admin/myzone/calendar" },
  { label: "Request Policy", icon: DescriptionOutlinedIcon, path: null },
];

// Registry `type` -> friendly label + row icon/colour for the Recent table.
const TYPE_META = {
  leave: { label: "Leave", icon: BeachAccessIcon, color: requestColors.leave },
  overtime: { label: "Overtime", icon: AccessTimeIcon, color: requestColors.overtime },
  offDayDuty: { label: "Off-Day Duty", icon: CalendarMonthIcon, color: requestColors.offday },
  workAdjustment: { label: "Attendance", icon: PersonPinCircleIcon, color: requestColors.attendance },
  advanceSalary: { label: "Finance", icon: PaymentsIcon, color: requestColors.finance },
};

const getRequestMeta = (request) =>
  TYPE_META[request.type] || {
    label: request.type,
    icon: DescriptionOutlinedIcon,
    color: designColors.slate500,
  };

const RecentRequestCard = ({ request }) => {
  const color = statusMeta(request.status);
  const meta = getRequestMeta(request);
  const RowIcon = meta.icon;

  return (
    <Box
      sx={{
        p: 1.5,
        border: `1px solid ${designColors.slate200}`,
        borderRadius: designRadii.card,
        backgroundColor: designColors.white,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            bgcolor: `${meta.color}1A`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <RowIcon sx={{ color: meta.color, fontSize: 19 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            sx={{ gap: 1 }}
          >
            <Typography
              variant="body2"
              fontWeight={designWeights.semibold}
              sx={{ color: designColors.slate900, minWidth: 0 }}
            >
              {meta.label}
            </Typography>
            <Chip
              size="small"
              label={request.status}
              sx={{
                bgcolor: `${color}1A`,
                color,
                fontWeight: designWeights.semibold,
                borderRadius: 1.5,
                flexShrink: 0,
              }}
            />
          </Stack>
          <Typography
            variant="body2"
            fontWeight={designWeights.medium}
            sx={{
              mt: 0.75,
              color: designColors.slate900,
              overflowWrap: "anywhere",
            }}
          >
            {request.title}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: "block",
              color: designColors.slate600,
              overflowWrap: "anywhere",
            }}
          >
            {request.subtitle || "-"}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export const MyRequestsHome = ({ onNavigate }) => {
  const history = useHistory();
  const { pending } = useMyPendingRequests();
  const [leaveDetailsOpen, setLeaveDetailsOpen] = useState(false);

  const go = (key) => onNavigate && onNavigate(key);

  // Summary counts. Pending is authoritative (the shared source is pending-only);
  // the other buckets fill in from whatever statuses the flat list carries.
  const summaryCounts = useMemo(() => {
    const c = { Pending: 0, Approved: 0, Rejected: 0, Cancelled: 0 };
    pending.forEach((r) => {
      const s = (r.status || "").trim();
      if (c[s] !== undefined) c[s] += 1;
    });
    c.Pending = pending.length;
    return c;
  }, [pending]);

  const recent = useMemo(() => pending.slice(0, 5), [pending]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
        <Box
          sx={{
            width: 50,
            height: 50,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${designColors.tealTint} 0%, #F4FBF8 100%)`,
            color: designColors.tealDark,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AssignmentOutlinedIcon sx={{ fontSize: 31 }} />
        </Box>
        <Box>
          <Typography sx={designType.pageTitle}>My Requests</Typography>
          <Typography sx={designType.pageSubtitle}>
            Raise new requests and track their status.
          </Typography>
        </Box>
      </Stack>

      {/* Quick Links - horizontal bar */}
      <Box sx={{ ...requestCardSx, mb: 2.5 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={{ xs: 1.5, md: 3 }}
        >
          <Typography
            fontWeight={designWeights.heading}
            sx={{ color: designColors.slate900, flexShrink: 0 }}
          >
            Quick Links
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            sx={{ rowGap: 1.5, columnGap: { xs: 2, md: 4 } }}
          >
            {quickLinks.map((q) => {
              const Icon = q.icon;
              return (
                <Stack
                  key={q.label}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  onClick={() => {
                    if (q.action === "leaveDetails") setLeaveDetailsOpen(true);
                    else if (q.path) history.push(q.path);
                  }}
                  sx={{
                    cursor: q.path || q.action ? "pointer" : "default",
                    color: designColors.slate800,
                    transition: "color .15s ease",
                    "&:hover": { color: designColors.tealDark },
                    "&:hover .qlIcon": { color: designColors.tealDark },
                  }}
                >
                  <Icon className="qlIcon" sx={{ color: designColors.slate600, fontSize: 20 }} />
                  <Typography variant="body2" fontWeight={designWeights.medium} sx={{ color: "inherit" }}>
                    {q.label}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Box>

      {/* My Request Summary - horizontal stat cards */}
      <Typography
        sx={{ ...designType.sectionHeading, mb: 1.5 }}
      >
        My Request Summary
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {summaryTiles.map((t) => {
          return (
            <Grid item xs={6} sm={3} key={t.key}>
              <StatCard
                icon={t.icon}
                title={t.key}
                value={summaryCounts[t.key] ?? 0}
                detail="Requests"
                accent={t.accent}
                tint={t.tint}
              />
            </Grid>
          );
        })}
      </Grid>

      {/* Main content - full width */}
      <Box>
          <Typography
            sx={{ ...designType.sectionHeading, mb: 1.5 }}
          >
            What would you like to apply for?
          </Typography>

          {/* Primary apply cards */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            {applyCards.map((c) => {
              const Icon = c.icon;
              return (
                <Grid item xs={12} sm={4} key={c.title}>
                  <Box
                    onClick={() => go(c.key)}
                    sx={{
                      ...requestCardSx,
                      p: 1.75,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                      transition: "all .18s ease",
                      "&:hover": {
                        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
                        borderColor: c.color,
                      },
                    }}
                  >
                    {/* icon + title on one row */}
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.25 }}>
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          borderRadius: 1.8,
                          bgcolor: c.tint,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ color: c.color }} />
                      </Box>
                      <Typography fontWeight={designWeights.heading} sx={{ color: designColors.slate900, fontSize: 16 }}>
                        {c.title}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="body2"
                      sx={{ color: designColors.slate600, mb: 2, flexGrow: 1 }}
                    >
                      {c.desc}
                    </Typography>

                    <Divider sx={{ mb: 1.25, borderColor: designColors.slate200 }} />
                    <Typography
                      variant="body2"
                      fontWeight={designWeights.semibold}
                      sx={{ color: c.color, display: "flex", alignItems: "center" }}
                    >
                      {c.cta} <ChevronRightIcon sx={{ fontSize: 18, ml: 0.25 }} />
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {/* Attendance + Finance wide cards */}
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} md={7}>
              <GroupCard
                title="Attendance Requests"
                subtitle="Permission, On Duty, Missing Punch, Schedule Change and Comp Off"
                accent={requestColors.attendance}
                tint="#EFF8FF"
                headerIcon={AssignmentOutlinedIcon}
                tiles={attendanceTiles}
                columns={5}
                onClick={() => go(NAV.attendance)}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <GroupCard
                title="Finance Requests"
                subtitle="Salary advance and expense claims"
                accent={requestColors.overtime}
                tint="#FFFAEB"
                headerIcon={AccountBalanceWalletOutlinedIcon}
                tiles={financeTiles}
                columns={2}
                onClick={() => go(NAV.finance)}
              />
            </Grid>
          </Grid>

          {/* Recent requests */}
          <Box sx={{ ...requestCardSx }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1.5 }}
            >
              <Typography fontWeight={designWeights.heading} sx={{ color: designColors.slate900 }}>
                Recent Requests
              </Typography>
              <Typography
                variant="body2"
                fontWeight={designWeights.semibold}
                sx={{ color: designColors.tealDark, cursor: "pointer" }}
                onClick={() => go(NAV.leave)}
              >
                View All
              </Typography>
            </Stack>
            {recent.length === 0 ? (
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                <TableEmptyState dataLength={recent} colSpan={1} subtitle="No recent requests." />
              </Box>
            ) : (
              <Stack spacing={1.25} sx={{ display: { xs: "flex", sm: "none" } }}>
                {recent.map((r, i) => (
                  <RecentRequestCard key={`${r.type}-${r.id}-${i}`} request={r} />
                ))}
              </Stack>
            )}
            <TableContainer sx={{ display: { xs: "none", sm: "block" }, overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    {["Request Type", "Details", "Period", "Status"].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          color: designColors.slate600,
                          fontWeight: designWeights.semibold,
                          borderBottom: `1px solid ${designColors.slate200}`,
                          backgroundColor: designColors.slate100,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent.length === 0 ? (
                    <TableEmptyState
                      dataLength={recent}
                      colSpan={4}
                      subtitle="No recent requests."
                    />
                  ) : (
                    recent.map((r, i) => {
                      const color = statusMeta(r.status);
                      const meta = getRequestMeta(r);
                      const RowIcon = meta.icon;
                      return (
                        <TableRow key={`${r.type}-${r.id}-${i}`} hover>
                          <TableCell sx={{ borderBottom: `1px solid ${designColors.slate200}` }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Box
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: 1.5,
                                  bgcolor: `${meta.color}1A`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <RowIcon sx={{ color: meta.color, fontSize: 18 }} />
                              </Box>
                              <Typography variant="body2" fontWeight={designWeights.medium}>
                                {meta.label}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${designColors.slate200}` }}>
                            <Typography variant="body2" fontWeight={designWeights.medium}>
                              {r.title}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${designColors.slate200}` }}>
                            <Typography variant="body2" sx={{ color: designColors.slate600 }}>
                              {r.subtitle || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: `1px solid ${designColors.slate200}` }}>
                            <Chip
                              size="small"
                              label={r.status}
                              sx={{
                                bgcolor: `${color}1A`,
                                color,
                                fontWeight: designWeights.semibold,
                                borderRadius: 1.5,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {recent.length > 0 && (
              <Typography
                variant="caption"
                sx={{ color: designColors.slate600, display: "block", textAlign: "center", mt: 1.5 }}
              >
                Showing 1 to {recent.length} of {pending.length} requests
              </Typography>
            )}
          </Box>
      </Box>

      <LeaveDetailsDrawer
        open={leaveDetailsOpen}
        onClose={() => setLeaveDetailsOpen(false)}
      />
    </Box>
  );
};

// Wide grouped card (Attendance / Finance) with its mini action tiles laid out
// on a single row via a fixed-column grid.
const GroupCard = ({
  title,
  subtitle,
  accent,
  tint,
  headerIcon: HeaderIcon,
  tiles,
  columns,
  onClick,
}) => (
  <Box sx={{ ...requestCardSx }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      sx={{ mb: 1.75, cursor: "pointer" }}
      onClick={onClick}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 1.8,
            bgcolor: tint,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <HeaderIcon sx={{ color: accent }} />
        </Box>
        <Box>
          <Typography fontWeight={designWeights.heading} sx={{ color: designColors.slate900 }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: designColors.slate600 }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
      <ChevronRightIcon sx={{ color: designColors.slate500 }} />
    </Stack>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 1,
      }}
    >
      {tiles.map((t) => {
        const Icon = t.icon;
        return (
          <Box
            key={t.label}
            onClick={onClick}
            sx={{
              border: `1px solid ${designColors.slate200}`,
              borderRadius: designRadii.control,
              p: 1.25,
              textAlign: "center",
              cursor: "pointer",
              transition: "all .15s ease",
              "&:hover": { borderColor: accent, bgcolor: designColors.slateBg },
            }}
          >
            <Icon sx={{ color: accent, fontSize: 22, mb: 0.5 }} />
            <Typography variant="caption" fontWeight={designWeights.medium} display="block" noWrap>
              {t.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Box>
);
